// ==========================================
// RESET PASSWORD PAGE
// ==========================================
// PURPOSE:
// - Read reset token from URL
// - Let user enter a new password
// - Send token + new password to backend
// - Show success/error message
// ==========================================

import React, { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ui, uiHandlers } from "../uiStyles";

export default function ResetPassword() {
  // ==========================================
  // ROUTER HELPERS
  // ==========================================
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // ==========================================
  // TOKEN FROM URL
  // ==========================================
  // Example:
  // /reset-password?token=abc123
  // ==========================================
  const token = useMemo(() => searchParams.get("token") || "", [searchParams]);

  // ==========================================
  // STATE
  // ==========================================
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  // ==========================================
  // FORM SUBMIT HANDLER
  // ==========================================
  async function onSubmit(e) {
    e.preventDefault();
    setMsg("");
    setErr("");

    // ------------------------------------------
    // BASIC VALIDATION
    // ------------------------------------------
    if (!token) {
      setErr("Missing reset token.");
      return;
    }

    if (!newPassword || !confirmPassword) {
      setErr("Please complete both password fields.");
      return;
    }

    if (newPassword.length < 8) {
      setErr("New password must be at least 8 characters.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setErr("Passwords do not match.");
      return;
    }

    setBusy(true);

    try {
      // ==========================================
      // API CALL → BACKEND RESET PASSWORD ROUTE
      // ==========================================
      const res = await fetch("/api/auth/reset-password", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          token,
          new_password: newPassword,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data.detail || data.message || "Could not reset password.");
      }

      setMsg(data.message || "Password reset successfully.");

      // ------------------------------------------
      // OPTIONAL REDIRECT AFTER SUCCESS
      // ------------------------------------------
      setTimeout(() => {
        navigate("/login", { replace: true });
      }, 1200);
    } catch (error) {
      setErr(error.message || "Could not reset password.");
    } finally {
      setBusy(false);
    }
  }

  // ==========================================
  // UI RENDER
  // ==========================================
  return (
    <div style={{ ...ui.page, maxWidth: 760, paddingTop: 48 }}>
      <div style={{ ...ui.card, maxWidth: 460, margin: "40px auto 0" }}>
        {/* ======================================
            HEADER
        ====================================== */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: "#ffffff" }}>
            Reset password
          </div>
          <div
            style={{
              fontSize: 13,
              color: "#9fb0c3",
              marginTop: 6,
              lineHeight: 1.5,
            }}
          >
            Enter your new password below to finish resetting your account.
          </div>
        </div>

        {/* ======================================
            SUCCESS MESSAGE
        ====================================== */}
        {msg && (
          <div
            style={{
              marginBottom: 14,
              padding: "10px 12px",
              borderRadius: 10,
              border: "1px solid rgba(74,222,128,0.30)",
              background: "rgba(74,222,128,0.08)",
              color: "#bbf7d0",
              fontSize: 13,
            }}
          >
            {msg}
          </div>
        )}

        {/* ======================================
            ERROR MESSAGE
        ====================================== */}
        {err && (
          <div
            style={{
              marginBottom: 14,
              padding: "10px 12px",
              borderRadius: 10,
              border: "1px solid rgba(248,113,113,0.35)",
              background: "rgba(248,113,113,0.08)",
              color: "#fca5a5",
              fontSize: 13,
            }}
          >
            {err}
          </div>
        )}

        {/* ======================================
            MISSING TOKEN WARNING
        ====================================== */}
        {!token && (
          <div
            style={{
              marginBottom: 14,
              padding: "10px 12px",
              borderRadius: 10,
              border: "1px solid rgba(255,255,255,0.10)",
              background: "rgba(255,255,255,0.03)",
              color: "#dbeafe",
              fontSize: 13,
            }}
          >
            This reset link is missing a token. Please request a new password reset link.
          </div>
        )}

        {/* ======================================
            FORM
        ====================================== */}
        <form onSubmit={onSubmit} style={{ display: "grid", gap: 14 }}>
          <input
            style={ui.input}
            {...uiHandlers.focus}
            type="password"
            placeholder="New password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
          />

          <input
            style={ui.input}
            {...uiHandlers.focus}
            type="password"
            placeholder="Confirm new password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
          />

          <button
            type="submit"
            disabled={busy || !token}
            style={{
              ...ui.btnPrimary,
              width: "100%",
              opacity: busy || !token ? 0.8 : 1,
              cursor: busy || !token ? "not-allowed" : "pointer",
            }}
            {...(!busy && token ? uiHandlers.primaryHover : {})}
          >
            {busy ? "Saving…" : "Reset password"}
          </button>
        </form>

        {/* ======================================
            NAVIGATION
        ====================================== */}
        <div style={{ textAlign: "center", marginTop: 16 }}>
          <Link to="/login" style={ui.link}>
            Back to sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
